import { ADUModel, PoolModel, DesignObject } from '../store/designStore'
import { ProjectQuote } from './pricingEngine'

// API Configuration
const getApiBaseUrl = () => {
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL
  }
  
  // Production detection
  if (import.meta.env.PROD || window.location.hostname.includes('serviceflow-pro.com')) {
    return 'https://api.serviceflow-pro.com'
  }
  
  // Development fallback
  return 'http://localhost:3001'
}

const getAiVisionBaseUrl = () => {
  if (import.meta.env.VITE_AI_VISION_URL) {
    return import.meta.env.VITE_AI_VISION_URL
  }
  
  // Always use the deployed AI vision service (both dev and prod)
  return 'https://serviceflow-ai-vision-564643876618.us-central1.run.app'
}

const API_BASE_URL = getApiBaseUrl()
const API_VERSION = import.meta.env.VITE_API_VERSION || 'v1'
const AI_VISION_URL = getAiVisionBaseUrl()

// Types for API requests/responses
export interface SaveDesignRequest {
  sessionId: string
  designType: 'adu' | 'pool'
  designData: {
    position: {
      x: number
      y: number
      z?: number
    }
    rotation?: number
    customizations: Record<string, any>
  }
}

export interface SaveDesignResponse {
  id: string
  name: string
  createdAt: string
  updatedAt: string
  objects: DesignObject[]
  metadata?: any
}

export interface LoadDesignResponse extends SaveDesignResponse {}

export interface GenerateQuoteRequest {
  designId?: string
  objects: DesignObject[]
  customerInfo?: {
    name?: string
    email?: string
    phone?: string
    address?: string
  }
  options?: {
    region?: string
    taxRate?: number
    includePermits?: boolean
  }
}

export interface GenerateQuoteResponse extends ProjectQuote {
  id: string
  designId?: string
  createdAt: string
  expiresAt: string
  status: 'draft' | 'sent' | 'accepted' | 'expired'
}

export interface AnalyzeImageRequest {
  image: File | Blob
  options?: {
    detectObjects?: boolean
    extractDimensions?: boolean
    identifyStructures?: boolean
  }
}

export interface AnalyzeImageResponse {
  id: string
  status: 'processing' | 'completed' | 'failed'
  results?: {
    propertyBounds?: {
      width: number
      depth: number
      area: number
    }
    detectedObjects?: Array<{
      type: string
      confidence: number
      bounds: { x: number; y: number; width: number; height: number }
    }>
    suggestedPlacements?: Array<{
      type: 'ADU' | 'Pool'
      position: [number, number, number]
      confidence: number
      reasoning: string
    }>
    structures?: Array<{
      type: string
      position: [number, number, number]
      dimensions: { width: number; height: number; depth: number }
    }>
  }
  error?: string
}

class ApiService {
  private async request<T>(
    endpoint: string, 
    options: RequestInit = {},
    baseUrl: string = API_BASE_URL
  ): Promise<T> {
    const url = `${baseUrl}${endpoint}`
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'X-Dev-Bypass': 'true', // Development bypass for testing
    }

    // Add auth token if available
    const token = localStorage.getItem('auth_token')
    if (token) {
      (defaultHeaders as any)['Authorization'] = `Bearer ${token}`
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error)
      throw error
    }
  }

  // Design Management
  async saveDesign(design: SaveDesignRequest): Promise<SaveDesignResponse> {
    return this.request<SaveDesignResponse>(`/api/${API_VERSION}/designs`, {
      method: 'POST',
      body: JSON.stringify(design),
    })
  }

  async loadDesign(designId: string): Promise<LoadDesignResponse> {
    return this.request<LoadDesignResponse>(`/api/${API_VERSION}/designs/${designId}`)
  }

  async updateDesign(designId: string, design: Partial<SaveDesignRequest>): Promise<SaveDesignResponse> {
    return this.request<SaveDesignResponse>(`/api/${API_VERSION}/designs/${designId}`, {
      method: 'PUT',
      body: JSON.stringify(design),
    })
  }

  async deleteDesign(designId: string): Promise<void> {
    return this.request<void>(`/api/${API_VERSION}/designs/${designId}`, {
      method: 'DELETE',
    })
  }

  async listDesigns(limit: number = 20, offset: number = 0): Promise<{
    designs: SaveDesignResponse[]
    total: number
    hasMore: boolean
  }> {
    const response = await this.request<{
      success: boolean
      data: SaveDesignResponse[]
      pagination: {
        limit: number
        offset: number
        total: number
      }
    }>(`/api/${API_VERSION}/designs?limit=${limit}&offset=${offset}`)
    
    return {
      designs: response.data || [],
      total: response.pagination?.total || 0,
      hasMore: (response.pagination?.offset || 0) + (response.pagination?.limit || 0) < (response.pagination?.total || 0)
    }
  }

  // Quote Management
  async generateQuote(request: GenerateQuoteRequest): Promise<GenerateQuoteResponse> {
    return this.request<GenerateQuoteResponse>(`/api/${API_VERSION}/quotes`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  async getQuote(quoteId: string): Promise<GenerateQuoteResponse> {
    return this.request<GenerateQuoteResponse>(`/api/${API_VERSION}/quotes/${quoteId}`)
  }

  async updateQuote(quoteId: string, updates: Partial<GenerateQuoteRequest>): Promise<GenerateQuoteResponse> {
    return this.request<GenerateQuoteResponse>(`/api/${API_VERSION}/quotes/${quoteId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  async sendQuote(quoteId: string, customerEmail: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(`/api/${API_VERSION}/quotes/${quoteId}/send`, {
      method: 'POST',
      body: JSON.stringify({ email: customerEmail }),
    })
  }

  // AI Vision Service
  async analyzeImage(request: AnalyzeImageRequest): Promise<AnalyzeImageResponse> {
    const formData = new FormData()
    formData.append('image', request.image)
    
    if (request.options) {
      formData.append('options', JSON.stringify(request.options))
    }

    return this.request<AnalyzeImageResponse>('/analyze', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    }, AI_VISION_URL)
  }

  async getAnalysisStatus(analysisId: string): Promise<AnalyzeImageResponse> {
    return this.request<AnalyzeImageResponse>(`/analysis/${analysisId}`, {}, AI_VISION_URL)
  }

  // File Upload
  async uploadImage(file: File, purpose: 'property' | 'reference' = 'property'): Promise<{
    id: string
    url: string
    filename: string
    size: number
    uploadedAt: string
  }> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('purpose', purpose)

    return this.request<{
      id: string
      url: string
      filename: string
      size: number
      uploadedAt: string
    }>('/api/upload', {
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    })
  }

  // Health Check
  async healthCheck(): Promise<{
    status: 'healthy' | 'unhealthy'
    services: {
      database: boolean
      aiVision: boolean
      fileStorage: boolean
    }
    version: string
    uptime: number
  }> {
    return this.request<{
      status: 'healthy' | 'unhealthy'
      services: {
        database: boolean
        aiVision: boolean
        fileStorage: boolean
      }
      version: string
      uptime: number
    }>('/api/health')
  }

  // User Authentication (if needed)
  async login(email: string, password: string): Promise<{
    token: string
    user: {
      id: string
      email: string
      name: string
      role: string
    }
  }> {
    const response = await this.request<{
      token: string
      user: {
        id: string
        email: string
        name: string
        role: string
      }
    }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })

    // Store token for future requests
    localStorage.setItem('auth_token', response.token)
    
    return response
  }

  async logout(): Promise<void> {
    localStorage.removeItem('auth_token')
    return this.request<void>('/api/auth/logout', {
      method: 'POST',
    })
  }

  async getCurrentUser(): Promise<{
    id: string
    email: string
    name: string
    role: string
  } | null> {
    try {
      return await this.request<{
        id: string
        email: string
        name: string
        role: string
      }>('/api/auth/me')
    } catch (error) {
      // If unauthorized, clear token
      localStorage.removeItem('auth_token')
      return null
    }
  }
}

// Export singleton instance
export const apiService = new ApiService()

// Export utility functions
export const isApiAvailable = async (): Promise<boolean> => {
  try {
    await apiService.healthCheck()
    return true
  } catch (error) {
    console.warn('API service unavailable:', error)
    return false
  }
}

export const withApiFallback = async <T>(
  apiCall: () => Promise<T>,
  fallback: () => T
): Promise<T> => {
  try {
    return await apiCall()
  } catch (error) {
    console.warn('API call failed, using fallback:', error)
    return fallback()
  }
}

export default apiService
